import React, { PureComponent } from "react";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  ResponsiveContainer,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  CartesianGrid,
} from "recharts";
import { getColor } from "../../tools";

export const CustomTooltip = ({
  active,
  payload,
  label,
  quantitativeLabelsChecked,
  qualitativeLabelsChecked,
  type = "lineChart",
}) => {
  const theme = useTheme();
  const labelWidth = 200;
  const answerWidth = 300;
  if (active) {
    const payloadArr = payload && payload.length > 0 ? payload[0].payload : {};
    return (
      <Paper
        style={{
          padding: "16px",
          maxWidth: 500,
        }}
        elevation={15}
      >
        <Typography variant="subtitle2">{`${moment
          .utc(label)
          .format("dd DD MMMM, HH:mm")}`}</Typography>

        {quantitativeLabelsChecked.map(({ id }) => {
          const relValue =
            type === "stacked"
              ? payloadArr[`${id}#stacked`]
              : payloadArr[`${id}#relative`];
          const getAbsValue = () => {
            const absValue = payloadArr[`${id}#absolute`];
            /* const type = payloadArr[`${id}#type`];
            if (type === "time")
              return moment.utc(absValue * 1000).format("HH:mm"); */
            return absValue;
          };
          const labelTitle = payloadArr[`${id}#title`];
          if (relValue !== undefined && relValue !== null)
            return (
              <div
                key={`LineChartQuantitative${id}`}
                style={{ display: "flex" }}
              >
                <div
                  style={{
                    height: "12px",
                    width: "12px",
                    backgroundColor: getColor(id),
                    marginRight: "8px",
                    marginTop: "3px",
                    borderRadius: "50%",
                    display: "flex",
                  }}
                ></div>
                <div>
                  <Box fontStyle="italic" width={labelWidth}>
                    <Typography variant="body2" noWrap>
                      {labelTitle}
                    </Typography>
                  </Box>
                </div>
                <div style={{ maxWidth: answerWidth, display: "flex", gap: 4 }}>
                  <Typography variant="subtitle2">{getAbsValue()}</Typography>
                  <Typography variant="body2">({relValue}%)</Typography>
                </div>
              </div>
            );
          return null;
        })}

        {qualitativeLabelsChecked.map(({ id }) => {
          const labelValue = payloadArr[`${id}#relative`];
          const labelTitle = payloadArr[`${id}#title`];
          if (labelValue !== undefined && labelValue !== null)
            return (
              <div
                key={`LineChartQualitative${id}`}
                style={{ display: "flex", paddingTop: 8 }}
              >
                <div
                  style={{
                    height: "12px",
                    width: "12px",
                    marginRight: "8px",
                    marginTop: "3px",
                    borderRadius: "50%",
                    display: "flex",
                  }}
                ></div>
                <div>
                  <Box fontStyle="italic" width={labelWidth}>
                    <Typography variant="body2" noWrap>
                      {labelTitle}
                    </Typography>
                  </Box>
                </div>
                <div>
                  <Typography
                    variant="body2"
                    style={{ maxWidth: answerWidth, wordBreak: "break-word" }}
                  >
                    {labelValue}
                  </Typography>
                </div>
              </div>
            );
          return null;
        })}
      </Paper>
    );
  }
  return null;
};

export class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, payload } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {moment.utc(payload.value).format("DD/MM")}
          </tspan>
          <tspan
            textAnchor="middle"
            x="0"
            dy="20"
            style={{ fontSize: "0.7rem" }}
          >
            {moment.utc(payload.value).format("HH:mm")}
          </tspan>
        </text>
      </g>
    );
  }
}

const getYDomain = ({ quantitativeLabelsChecked }) => {
  const minIntValues = quantitativeLabelsChecked.map(
    (el) => el.item?.itemExtra?.minInt ?? 0
  );
  const maxIntValues = quantitativeLabelsChecked.map(
    (el) => el.item?.itemExtra?.maxInt ?? 100
  );
  const areMinIntValuesTheSame =
    minIntValues.length > 0
      ? minIntValues.every((val) => val === minIntValues[0])
      : false;
  const areMaxIntValuesTheSame =
    maxIntValues.length > 0
      ? maxIntValues.every((val) => val === maxIntValues[0])
      : false;
  const minInt = areMinIntValuesTheSame ? minIntValues[0] : 0;
  const maxInt = areMaxIntValuesTheSame ? maxIntValues[0] : 100;
  const unit = minInt === 0 && maxInt === 100 ? "%" : "";
  return { domain: [minInt, maxInt], unit };
};

function TimeSeriesChart(props) {
  const theme = useTheme();
  const {
    data,
    time,
    quantitativeLabelsChecked,
    qualitativeLabelsChecked,
    opacity,
    height,
    reference,
    connectLine = true,
  } = props;

  return (
    <div>
      <ResponsiveContainer height={height}>
        <LineChart
          data={data}
          cursor={"default"}
          margin={{ top: 10, right: 10 }}
        >
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={theme.palette.primary.light}
                stopOpacity={0.1}
              />
              <stop
                offset="95%"
                stopColor={theme.palette.primary.light}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="time"
            height={60}
            domain={["dataMin", "dataMax"]}
            name="Time"
            tick={<CustomizedAxisTick />}
            //interval={time ? "preserveStartEnd" : 0} // 0: all the ticks will be shown
            scale={time ? "time" : "auto"}
            type={time ? "number" : "category"}
            minTickGap={10}
          />

          <YAxis
            dataKey="value"
            domain={getYDomain({ quantitativeLabelsChecked }).domain}
            interval={0}
            unit={getYDomain({ quantitativeLabelsChecked }).unit}
          />

          <Tooltip
            content={
              <CustomTooltip
                quantitativeLabelsChecked={quantitativeLabelsChecked}
                qualitativeLabelsChecked={qualitativeLabelsChecked}
              />
            }
            wrapperStyle={{ zIndex: 1 }}
          />

          {reference ? <ReferenceLine x={reference} stroke="#0003" /> : null}
          {quantitativeLabelsChecked.map(({ id, label }) => (
            <Line
              connectNulls={connectLine}
              key={`${id}Line`}
              type="monotone"
              dataKey={
                getYDomain({ quantitativeLabelsChecked }).unit === "%"
                  ? `${id}#relative`
                  : `${id}#absolute`
              }
              stroke={getColor(id)}
              strokeOpacity={`${opacity ? opacity[id] : 1}`}
              strokeWidth={2}
              dot={{ fill: "#fff", r: 2 }}
              activeDot={{
                r: 4,
              }}
              isAnimationActive={false}
            />
          ))}
          <Line
            type="basis"
            dataKey={"time"}
            stroke="0"
            dot={{ r: 0 }}
            activeDot={{
              r: 0,
            }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default TimeSeriesChart;
